import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  DataTable,
  DataTableSkeleton,
  InlineNotification,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
} from '@carbon/react';
import EmptyState from 'src/components/Base/EmptyState';
import { authClient } from 'src/services/auth/client';
import { clearAccessToken } from 'src/services/auth/tokenStore';
import { QUERIES } from 'src/utils/react-query';

type AdminUserRow = {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  role?: string | null;
  banned: boolean;
  createdAt: Date | string;
};

const AdminUsers = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [verifyTarget, setVerifyTarget] = useState<AdminUserRow | null>(null);
  const [verifyPending, setVerifyPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function mutationError(e: unknown): string {
    return e instanceof Error && e.message ? e.message : t('admin.error', { message: '' });
  }

  const { data: page, isLoading } = useQuery({
    queryKey: [QUERIES.ADMIN_USERS, search],
    queryFn: async () => {
      const { data, error } = await authClient.admin.listUsers({
        query: { searchValue: search || undefined, limit: 25, offset: 0 },
      });
      if (error) throw new Error(error.message);
      return data as { users: AdminUserRow[]; total: number };
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERIES.ADMIN_USERS] });
  const ban = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      try {
        const { error: apiError } = await authClient.admin.banUser({
          userId,
          banReason: t('admin.banReason'),
        });
        if (apiError) throw new Error(apiError.message);
      } catch (e) {
        setError(mutationError(e));
        throw e;
      }
    },
    onSuccess: invalidate,
  });
  const unban = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      try {
        const { error: apiError } = await authClient.admin.unbanUser({ userId });
        if (apiError) throw new Error(apiError.message);
      } catch (e) {
        setError(mutationError(e));
        throw e;
      }
    },
    onSuccess: invalidate,
  });
  const impersonate = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      try {
        const { error: apiError } = await authClient.admin.impersonateUser({ userId });
        if (apiError) throw new Error(apiError.message);
        // The cached JWT still carries the admin's own identity otherwise.
        clearAccessToken();
        navigate('/', { replace: true });
      } catch (e) {
        setError(mutationError(e));
        throw e;
      }
    },
  });

  async function markVerified() {
    if (!verifyTarget) return;
    setError(null);
    setVerifyPending(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_AUTH_URL}/internal/admin/verify-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ userId: verifyTarget.id }),
      });
      if (!response.ok) throw new Error(`Verify request failed: ${response.status}`);
      setVerifyTarget(null);
      invalidate();
    } catch (e) {
      setError(mutationError(e));
    } finally {
      setVerifyPending(false);
    }
  }

  const headers = [
    { key: 'email', header: t('common.email') },
    { key: 'name', header: t('common.name') },
    { key: 'role', header: t('common.role') },
    { key: 'status', header: t('admin.columns.status') },
    { key: 'createdAt', header: t('admin.columns.createdAt') },
  ];

  if (isLoading) {
    return <DataTableSkeleton columnCount={6} rowCount={10} headers={headers} />;
  }

  const rows =
    page?.users.map((user) => ({
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role ?? t('admin.roles.user'),
      status: user.banned ? t('admin.columns.banned') : t('admin.columns.active'),
      createdAt: dayjs(user.createdAt).format('DD/MM/YYYY'),
    })) ?? [];

  return (
    <>
      {error && <InlineNotification kind="error" title={error} lowContrast hideCloseButton />}
      <TextInput
        id="admin-users-search"
        labelText={t('admin.users.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <DataTable rows={rows} headers={headers}>
        {({ rows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer title={t('admin.users.title')} description={t('admin.users.subtitle')}>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                  <TableHeader>{t('common.actions')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const user = page?.users[index];
                  return (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                      <TableCell>
                        {user && (
                          <>
                            {user.banned ? (
                              <Button
                                kind="ghost"
                                size="sm"
                                disabled={unban.isPending}
                                onClick={() => unban.mutate(user.id)}
                              >
                                {t('admin.unban')}
                              </Button>
                            ) : (
                              <Button
                                kind="danger--ghost"
                                size="sm"
                                disabled={ban.isPending}
                                onClick={() => ban.mutate(user.id)}
                              >
                                {t('admin.ban')}
                              </Button>
                            )}
                            <Button
                              kind="ghost"
                              size="sm"
                              disabled={impersonate.isPending}
                              onClick={() => impersonate.mutate(user.id)}
                            >
                              {t('admin.impersonate')}
                            </Button>
                            {!user.emailVerified && (
                              <Button kind="ghost" size="sm" onClick={() => setVerifyTarget(user)}>
                                {t('admin.markVerified')}
                              </Button>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {rows.length === 0 && <EmptyState />}
          </TableContainer>
        )}
      </DataTable>
      <Modal
        open={!!verifyTarget}
        modalHeading={t('admin.markVerifyTitle')}
        modalLabel={verifyTarget?.email ?? ''}
        primaryButtonText={verifyPending ? t('common.loading') : t('admin.markVerified')}
        secondaryButtonText={t('actions.cancel')}
        primaryButtonDisabled={verifyPending}
        onRequestClose={() => setVerifyTarget(null)}
        onSecondarySubmit={() => setVerifyTarget(null)}
        onRequestSubmit={markVerified}
      >
        <p>{t('admin.markVerifiedConfirm', { email: verifyTarget?.email ?? '' })}</p>
      </Modal>
    </>
  );
});

export default AdminUsers;
